import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import { ISurveyHeader } from "../../../../common/data/dtos/survey-header";
import { ISurveyHeaderItem } from "../../../../common/data/dtos/survey-header-item";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import InputGroup from "../../../../components/bootstrap/forms/InputGroup";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import { generateTempId } from "../../../../helpers/helpers";
import surveyHeaderItemService from "../../../../services/survey-header-item-service";

type SurveyHeaderFormValues = {
    id?: string;
    item: string;
};

interface Props {
    isModalOpened: boolean;
    selectedSurvey: ISurvey | null;
    selectedSurveyHeader: ISurveyHeader | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const SurveyHeaderItemModal = ({ isModalOpened, selectedSurvey, selectedSurveyHeader, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [surveyHeaderItems, setSurveyHeaderItems] = useState<ISurveyHeaderItem[]>([]);

    const formik = useFormik({
        initialValues: {
            item: '',
        },

        onSubmit: (values, { resetForm }) => {
            handleAddClick(values);
            resetForm();
        },
    });

    useEffect(() => {
        resetForm();

        if (selectedSurveyHeader) {
            fetchSurveyHeaderItems();
        }

    }, [isModalOpened]);

    const fetchSurveyHeaderItems = async () => {

        setIsSubmitting(true);

        if (!selectedSurveyHeader) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsSubmitting(false);
        } else {
            const response = await surveyHeaderItemService.getSurveyHeaderItemBySurveyHeader(selectedSurveyHeader.id!);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setSurveyHeaderItems(response.surveyHeaderItems || []);
        }
    };

    const onFormSubmit = async (values: SurveyHeaderFormValues) => {
        if (!surveyHeaderItems) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION)
            return;
        }

        const itensToAdd = surveyHeaderItems
            .filter(item => !item.id || item.id.trim() === '');


        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD)
            return;
        }

        setIsSubmitting(true);

        const response = await surveyHeaderItemService.createManySurveyHeaderItem(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm()
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                item: '',
            },
        });
    };

    const handleAddClick = async (values: SurveyHeaderFormValues) => {

        if (!values.item || !selectedSurveyHeader) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const newSurveyHeaderItem: ISurveyHeaderItem = {
            item: values.item,
            surveyHeaderId: selectedSurveyHeader.id!,
            rowVersion: 1,
            status: StatusEnum.Active,
            tempId: generateTempId()

        };

        setSurveyHeaderItems((prev) => [
            ...prev,
            newSurveyHeaderItem,
        ]);
    }

    const handleDeleteClick = async (surveyHeaderItem: ISurveyHeaderItem) => {

        if (!surveyHeaderItem.id) {
            const filteredList = surveyHeaderItems.filter(item => item.tempId !== surveyHeaderItem.tempId);
            setSurveyHeaderItems(filteredList);
        } else {

            setIsSubmitting(true);

            const response = await surveyHeaderItemService.deleteSurveyHeaderItem(surveyHeaderItem.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchSurveyHeaderItems();
        }
    }

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='survey-header-item-modal'>Manutenção de itens de cabeçalhos</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados dos itens de cabeçalho' icon='ClearAll'>
                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <InputGroup
                                    isWrap={true}
                                >
                                    <FormGroup id='item' label='Item do Cabeçalho' isFloating>
                                        <Input
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.item}
                                        />
                                    </FormGroup>
                                    <Button
                                        icon='AddCircleOutline'
                                        color='primary'
                                        onClick={formik.handleSubmit}
                                    >
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12 table-responsive '>

                                <table className='table table-modern table-hover'>
                                    <thead>
                                        <tr>
                                            <th>Item do Cabeçalho</th>
                                            <td aria-labelledby='Actions' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {surveyHeaderItems
                                            .map((item: ISurveyHeaderItem) => (
                                                <tr key={item.id}>
                                                    <td>{item.item}</td>

                                                    <td className='text-end'>
                                                        <Button
                                                            color='dark'
                                                            isLight
                                                            icon='Delete'
                                                            onClick={() => handleDeleteClick(item)}
                                                            aria-label='Remover'
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};